<template>
  <b-modal
    id="modal-order-view"
    ref="modal"
    v-model="open"
    size="ov-custom"
    centered
    hide-footer
    title="Manage Status"
    @hidden="onModalClose"
  >
    <b-overlay
      :show="modalLoading"
      blur="10px"
      opacity="0.30"
      rounded="sm"
      variant="light"
    >
      <ValidationObserver
        ref="manageStatus"
        v-slot="{ handleSubmit }"
        slim
      >
        <b-form
          class="my-8"
          @submit.prevent="handleSubmit(onClickManageStatus)"
        >
          <div class="my-1 font-weight-bold">
            Basic information
          </div>
          <b-row>
            <b-col md="6">
              <b-form-group
                class="required status-form-label"
                label="Status Name"
                label-for="h-name"
              >
                <b-form-input
                  id="name"
                  v-model="statusOptions.name"
                  placeholder="Enter Name"
                  type="text"
                  readonly
                />
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group
                class="required status-form-label"
                label="Custom Name"
                label-for="h-customName"
              >
                <b-form-input
                  id="customName"
                  v-model="customStatusName"
                  placeholder="Enter Custom Name"
                  type="text"
                />
              </b-form-group>
            </b-col>
          </b-row>
          <b-row>
            <b-col md="6">
              <b-form-group
                class="required status-form-label"
                label="Category"
                label-for="h-category"
              >
                <v-select
                  v-model="statusOptions.category"
                  :options="categoryOptions"
                  :reduce="option => option.value"
                  label="name"
                  autocomplete="off"
                  placeholder="Select Category"
                  :clearable="false"
                >
                  <template v-slot:option="option">
                    {{ option.name }}
                  </template>
                </v-select>
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group
                class="required status-form-label"
                label="Color"
                label-for="h-color"
              >
                <v-select
                  v-model="statusOptions.color"
                  :options="colorOptions"
                  :reduce="option => option.value"
                  label="name"
                  autocomplete="off"
                  placeholder="Select Color"
                  class="color-options-dropdown"
                  :clearable="false"
                >
                  <template v-slot:option="option">
                    <div
                      class="status-color-option"
                      :style="{ '--option-color': option.color }"
                    >
                      <span
                        class="color-dot"
                        :style="{ backgroundColor: option.color }"
                      />
                      {{ option.name }}
                    </div>
                  </template>
                </v-select>
              </b-form-group>
            </b-col>
          </b-row>
          <b-row>
            <b-col md="12">
              <b-form-group
                class="required status-form-label"
                label="Description"
                label-for="h-description"
              >
                <b-form-textarea
                  id="description"
                  v-model="statusOptions.description"
                  placeholder="Enter Description"
                  type="text"
                />
              </b-form-group>
            </b-col>
          </b-row>
          <div class="my-1 font-weight-bold">
            Status configuration
          </div>
          <b-row v-if="!isCustomStatus">
            <b-col md="6">
              <b-form-group
                class="required status-form-label"
                label="Before Default Statuses"
                label-for="h-beforeDefaultStatuses"
              >
                <v-select
                  v-model="statusOptions.before_default_statuses"
                  :options="beforeDefaultStatusOptions"
                  :reduce="option => option.key"
                  label="name"
                  placeholder="Select Status"
                  multiple
                  :deselect-from-dropdown="false"
                  class="manage-status-dropdown"
                >
                  <template
                    v-slot:option="option"
                    class="text-truncate"
                  >
                    {{ option.name }}
                  </template>
                </v-select>
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group
                class="required status-form-label"
                label="After Default Statuses"
                label-for="h-afterDefaultStatuses"
              >
                <v-select
                  v-model="statusOptions.after_default_statuses"
                  :options="afterDefaultStatusOptions"
                  :reduce="option => option.key"
                  label="name"
                  placeholder="Select Status"
                  multiple
                  :deselect-from-dropdown="false"
                  class="manage-status-dropdown"
                >
                  <template
                    v-slot:option="option"
                    class="text-truncate"
                  >
                    {{ option.name }}
                  </template>
                </v-select>
              </b-form-group>
            </b-col>
          </b-row>
          <b-row>
            <b-col md="6">
              <b-form-group
                class="required status-form-label"
                label="Before Custom Statuses"
                label-for="h-beforeStatuses"
              >
                <v-select
                  v-model="statusOptions.before_custom_statuses"
                  :options="beforeCustomStatusOptions"
                  :reduce="option => option.key"
                  label="name"
                  placeholder="Select Status"
                  multiple
                  deselect-from-dropdown
                >
                  <template
                    v-slot:option="option"
                    class="text-truncate"
                  >
                    {{ option.name }}
                  </template>
                </v-select>
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group
                class="required status-form-label"
                label="After Custom Statuses"
                label-for="h-afterStatuses"
              >
                <v-select
                  v-model="statusOptions.after_custom_statuses"
                  :options="afterCustomStatusOptions"
                  :reduce="option => option.key"
                  label="name"
                  placeholder="Select Status"
                  multiple
                  deselect-from-dropdown
                >
                  <template
                    v-slot:option="option"
                    class="text-truncate"
                  >
                    {{ option.name }}
                  </template>
                </v-select>
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group
                class="required status-form-label"
                label="Icon"
                label-for="h-icon"
              >
                <icon-picker v-model="statusOptions.icon" />
              </b-form-group>
            </b-col>
          </b-row>
          <div class="my-1 font-weight-bold">
            Status settings
          </div>
          <b-row class="mt-1 status-settings-row">
            <b-col
              cols="6"
              class="status-setting-item"
            >
              <b-form-checkbox
                v-model="statusOptions.is_default"
                :value="true"
                :unchecked-value="false"
              >
                Default Status
              </b-form-checkbox>
            </b-col>
            <b-col
              v-if="statusOptions.key === 'key_1' || isCustomStatus"
              cols="6"
              class="status-setting-item"
            >
              <b-form-checkbox
                v-model="statusOptions.is_active"
                :value="true"
                :unchecked-value="false"
              >
                Active Status
              </b-form-checkbox>
            </b-col>
            <b-col
              v-if="!(statusOptions.key === 'key_1' || statusOptions.key === 'key_14' || statusOptions.key === 'key_26' || statusOptions.key === 'key_28' || statusOptions.key === 'key_29')"
              cols="6"
              class="status-setting-item"
            >
              <b-form-checkbox
                v-model="statusOptions.is_bulk_update"
                :value="true"
                :unchecked-value="false"
              >
                Allow Bulk Status Update
              </b-form-checkbox>
            </b-col>
            <b-col
              v-if="isCustomStatus"
              cols="6"
              class="status-setting-item"
            >
              <b-form-checkbox
                v-model="statusOptions.is_bypass_enabled"
                :value="true"
                :unchecked-value="false"
              >
                Allow Bypass
              </b-form-checkbox>
            </b-col>
            <b-col
              v-if="statusOptions.key === 'key_29'"
              cols="6"
              class="status-setting-item"
            >
              <b-form-checkbox
                v-model="statusOptions.settings.allow_false_update_from"
                :value="true"
                :unchecked-value="false"
              >
                Allow Direct Transition To Received At Destination Warehouse
              </b-form-checkbox>
            </b-col>
            <b-col
              v-if="(statusOptions.key === 'key_15' || statusOptions.key === 'key_17' || statusOptions.key === 'key_31')"
              cols="6"
              class="status-setting-item"
            >
              <b-form-checkbox
                id="checkbox-remember-reason"
                v-model="statusOptions.is_remember_reason_enabled"
                :value="true"
                :unchecked-value="false"
              >
                Enable Remember Reason
              </b-form-checkbox>
            </b-col>
          </b-row>
          <b-col
            v-if="statusOptions.key === 'key_11' || isCustomStatus"
            class="my-2 d-flex justify-content-center"
          >
            <b-button
              variant="text-secondary"
              class="p-0 mb-1"
              @click="showMore = !showMore"
            >
              {{ showMore ? 'See less' : 'See more' }}
              <feather-icon
                :icon="showMore ? 'ChevronUpIcon' : 'ChevronDownIcon'"
                size="15"
              />
            </b-button>
          </b-col>
          <div v-if="showMore">
            <div v-if="statusOptions.key === 'key_11' || isCustomStatus">
              <div class="my-1 font-weight-bold">
                Validations
              </div>
              <b-row class="mt-1">
                <b-col cols="6">
                  <b-form-checkbox
                    v-model="statusOptions.settings.any_destination_branch_enabled"
                    :value="true"
                    :unchecked-value="false"
                  >
                    Allow any destination branch
                  </b-form-checkbox>
                </b-col>
                <b-col
                  cols="6"
                >
                  <b-form-checkbox
                    v-model="statusOptions.settings.false_branch_update_enabled"
                    :value="true"
                    :unchecked-value="false"
                  >
                    Allow False Branch Update
                  </b-form-checkbox>
                </b-col>
                <b-col
                  v-if="isCustomStatus"
                  cols="6"
                >
                  <b-form-checkbox
                    v-model="statusOptions.settings.allow_false_update_from"
                    :value="true"
                    :unchecked-value="false"
                  >
                    Allow False Update From
                  </b-form-checkbox>
                </b-col>
                <b-col
                  v-if="isCustomStatus"
                  cols="6"
                >
                  <b-form-checkbox
                    v-model="statusOptions.settings.allow_false_update_to"
                    :value="true"
                    :unchecked-value="false"
                  >
                    Allow False Update To
                  </b-form-checkbox>
                </b-col>
              </b-row>
            </div>
            <!-- Actions Section -->
            <div v-if="isCustomStatus && statusOptions.actions && Object.keys(statusOptions.actions).length > 0">
              <div class="my-1 font-weight-bold">
                Actions
              </div>
              <b-row>
                <b-col
                  v-for="(value, key) in statusOptions.actions"
                  :key="key"
                  md="4"
                >
                  <b-form-checkbox
                    v-model="statusOptions.actions[key]"
                  >
                    {{ formatInput(key) }}
                  </b-form-checkbox>
                </b-col>
              </b-row>
            </div>
          </div>
          <b-col
            class="mb-2 mt-2 d-flex justify-content-center"
          >
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              type="submit"
              variant="primary"
              class="mr-1"
            >
              Submit
            </b-button>
            <b-button
              v-ripple.400="'rgba(186, 191, 199, 0.15)'"
              type="button"
              variant="outline-secondary"
              @click="clearAll"
            >
              Clear
            </b-button>
          </b-col>
        </b-form>
      </ValidationObserver>
    </b-overlay>
  </b-modal>
</template>

<script>
import {
  BModal,
  BOverlay,
  VBModal,
  BRow,
  BCol,
  BFormGroup,
  BForm,
  BFormInput,
  BFormCheckbox,
  BButton,
  BFormTextarea,
} from 'bootstrap-vue'
import SuccessMessage from '@core/mixins/SuccessMessage'
import ErrorMessage from '@core/mixins/ErrorMessage'
import Ripple from 'vue-ripple-directive'
import IconPicker from '@/components/Icons/IconPicker.vue'
import { RepositoryFactory } from '@/repository/RepositoryFactory'

const OrderRepository = RepositoryFactory.get('order')

export default {
  name: 'ManageStatus',
  directives: {
    'b-modal': VBModal,
    Ripple,
  },
  components: {
    BModal,
    BOverlay,
    BRow,
    BCol,
    BFormGroup,
    BForm,
    BFormInput,
    BFormCheckbox,
    // BFormCheckboxGroup,
    BButton,
    BFormTextarea,
    IconPicker,
  },
  mixins: [SuccessMessage, ErrorMessage],
  data() {
    return {
      open: false,
      signatureOpen: false,
      modalLoading: false,
      loading: false,
      id: null,
      items: {},
      perPage: 10,
      statusOptions: {
        settings: {},
        actions: {},
        key: '',
        is_bypass_enabled: false,
      },
      customStatusName: '',
      categoryOptions: [
        { name: 'First Mile', value: 'first' },
        { name: 'Middle Mile', value: 'middle' },
        { name: 'Last Mile', value: 'last' },
        { name: 'Merchant', value: 'merchant' },
      ],
      colorOptions: [
        { name: 'Primary', value: 'primary', color: '#007bff' },
        { name: 'Info', value: 'info', color: '#17a2b8' },
        { name: 'Danger', value: 'danger', color: '#dc3545' },
        { name: 'Success', value: 'success', color: '#28a745' },
        { name: 'Warning', value: 'warning', color: '#ffc107' },
        { name: 'Secondary', value: 'secondary', color: '#6c757d' },
      ],
      beforeDefaultStatusOptions: [],
      afterDefaultStatusOptions: [],
      beforeCustomStatusOptions: [],
      afterCustomStatusOptions: [],
      permissionGroupOptions: [],
      showMore: false,
    }
  },
  computed: {
    filterobject() {
      return Object.keys(this.statusOptions.actions).filter(key => this.statusOptions.actions[key])
    },
    isCustomStatus() {
      return this.statusOptions.key.startsWith('cus_')
    },
  },
  methods: {
    formatInput(key) {
      return key.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase())
    },
    async fetchStatusDetails() {
      try {
        const { data } = (await OrderRepository.updateStatusId(this.id)).data
        await this.fetchCustomBeforeAndAfterStatuses()
        if (!data.key.startsWith('cus_')) {
          await this.fetchDefaultBeforeAndAfterStatuses()
        }
        await this.$nextTick()
        this.statusOptions = data
        if (!this.statusOptions.settings) {
          this.statusOptions.settings = {}
        }
        if (!this.statusOptions.actions) {
          this.statusOptions.actions = {}
        }
        if (this.statusOptions.is_bypass_enabled == null) {
          this.$set(this.statusOptions, 'is_bypass_enabled', false)
        }
        this.customStatusName = this.statusOptions.name
      } catch (e) {
        this.convertAndNotifyError(e)
      }
    },
    async onClickManageStatus() {
      try {
        this.loading = true
        if (!this.statusOptions.settings.any_destination_branch_enabled && this.statusOptions.settings.false_branch_update_enabled) {
          this.showErrorMessage("Cannot enable the 'False Branch Update' validation without selecting the 'Allow any destination branch' validation.")
          this.loading = false
          return
        }
        const data = {
          status_data: this.statusOptions,
          name: this.customStatusName,
          category: this.statusOptions.category,
          color: this.statusOptions.color,
          description: this.statusOptions.description,
          before_custom_statuses: this.statusOptions.before_custom_statuses,
          after_custom_statuses: this.statusOptions.after_custom_statuses,
          icon: this.statusOptions.icon,
          is_default: this.statusOptions.is_default,
          is_active: this.statusOptions.is_active,
          is_bypass_enabled: this.statusOptions.is_bypass_enabled,
          actions: this.statusOptions.actions,
          settings: {
            any_destination_branch_enabled: this.statusOptions.settings.any_destination_branch_enabled,
            false_branch_update_enabled: this.statusOptions.settings.false_branch_update_enabled,
            allow_false_update_from: this.statusOptions.settings.allow_false_update_from,
            allow_false_update_to: this.statusOptions.settings.allow_false_update_to,
          },
        }
        const res = await OrderRepository.updateStatusIdupdate(this.statusOptions.id, data)
        if (res.status === 200) {
          this.open = false
          this.showSuccessMessage(`${this.statusOptions.original_name}  updated successfully`)
          this.$parent.onClickRefresh()
          localStorage.removeItem('order_statuses')
        } else {
          this.showErrorMessage(`Cannot Update ${this.statusOptions.original_name}  Status Settings`)
        }
      } catch (e) {
        this.convertAndNotifyError(e)
      }
      this.loading = false
    },
    async openModal(id) {
      this.open = true
      this.id = id
      this.modalLoading = true
      await this.fetchStatusDetails()
      await this.readPermissionGroups()
      this.modalLoading = false
    },
    onModalClose() {
      this.showMore = false
    },
    clearAll() {
      this.statusOptions.is_active = false
      this.statusOptions.is_bypass_enabled = false
      this.statusOptions.settings.any_destination_branch_enabled = false
      this.statusOptions.settings.false_branch_update_enabled = false
      this.statusOptions.settings.allow_false_update_from = false
      this.statusOptions.settings.allow_false_update_to = false
      this.customStatusName = ''
    },
    async fetchDefaultBeforeAndAfterStatuses() {
      const statusOptions = (await OrderRepository.getDefaultStatusDropdownList(this.id)).data
      this.beforeDefaultStatusOptions = statusOptions.before_default_statuses
      this.afterDefaultStatusOptions = statusOptions.after_default_statuses
    },
    async fetchCustomBeforeAndAfterStatuses() {
      const statusOptions = (await OrderRepository.getCustomStatusDropdownList(this.id)).data
      this.beforeCustomStatusOptions = statusOptions.before_custom_statuses
      this.afterCustomStatusOptions = statusOptions.after_custom_statuses
    },
    async readPermissionGroups() {
      this.permissionGroupOptions = (await OrderRepository.getStatusPermissionGroups()).data.data
    },
  },
}
</script>
<style scoped>
  @media (min-width: 767px) {
    .status-bar {
      padding : 1rem;
    }
    .status-badge {
      display: inline-block;
      white-space: normal;
    }
  }
.custom-control {
    position: relative;
    z-index: 1;
    display: block;
    min-height: 1.45rem;
    margin-bottom: 15px;
  }
.status-settings-row {
  row-gap: 8px;
}
.status-setting-item {
  display: flex;
  align-items: center;
}
.status-setting-item ::v-deep .custom-control {
  margin-bottom: 0;
}
.text-container {
  max-width: 100%; /* Set your desired maximum width */
  word-wrap: break-word; /* Break long words to the next line */
  white-space: normal; /* Allow whitespace to wrap */
}
.formCheckbox {
  margin-top: 8px;
}
.validation-label{
  color: #5e5873;
}
.color-options-dropdown ::v-deep .vs__dropdown-option {
  padding: 0 !important;
}
.status-color-option {
  padding: 6px 12px; /* Adjust these values if needed */
  display: flex;
  align-items: center;
}
.color-options-dropdown ::v-deep .vs__dropdown-option--highlight .status-color-option {
  background-color: var(--option-color);
  color: white; /* Change text color for better contrast */
}
.color-dot {
  display: inline-block;
  width: 12px;           /* Set the size of the dot */
  height: 12px;          /* Make it a square */
  border-radius: 50%;    /* Turn the square into a circle */
  margin-right: 10px;    /* Add space between the dot and the text */
}
</style>
